import { getTerms, TermType } from '~/domain/terms/terms.server';
import { storefrontContext } from '~/domain/core/index.server';
import type { Route } from './+types/route';
import type { RootLoaderData } from '~/root';
import { buildPageSeo } from '~/lib/seo';
import { localeHref } from '~/lib/use-locale-href';
import { isTermsType, type TermsType } from '~/domain/terms/types';

import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import type { RouteHandle } from '~/lib/header-config';
import { Markdown } from '~/ui/markdown';
import { CookieDeclaration } from '~/ui/cookie-consent/cookie-declaration';
import { isCookieConsentEnabled } from '~/domain/plugins/services/cookie-consent';
import { useRootData } from '~/root';
import { notFound } from '~/lib/react-router';
import { getLanguageCode } from '~/lib/localization/languages.server';
import { PageCard } from '~/ui/app-shell/layouts';
import { createServerIntl } from '~/lib/localization/server-intl.server';
import { defineMessage, type MessageDescriptor } from 'react-intl';

export const handle = {
  header: { backLink: '/' },
} satisfies RouteHandle;

const TERM_TYPE_BY_TERMS_TYPE: Record<TermsType, TermType> = {
  general: TermType.General,
  privacy: TermType.Privacy,
  cancellation: TermType.Cancellation,
};

export async function loader({ params, context }: Route.LoaderArgs) {
  const candidate = params.type === undefined ? 'general' : params.type;
  if (!isTermsType(candidate)) {
    throw notFound();
  }

  const storefront = context.get(storefrontContext);

  const terms = await getTerms(
    storefront.companyId,
    getLanguageCode(storefront.resolvedLocale)
  );
  if (!terms) {
    throw notFound();
  }

  const content = terms[TERM_TYPE_BY_TERMS_TYPE[candidate]]?.content ?? '';

  const intl = createServerIntl(context);
  const title = intl.formatMessage(TITLE_BY_TYPE[candidate]);

  return { content, title, type: candidate };
}

/**
 * Titles per term type, preserving the three original wordings/descriptions
 * so the extracted i18n IDs stay stable. They must be `defineMessage` calls
 * (not plain objects passed dynamically to `formatMessage`) so the formatjs
 * build plugin injects an `id` — otherwise `formatMessage` throws at runtime.
 */
const TITLE_BY_TYPE: Record<TermsType, MessageDescriptor> = {
  general: defineMessage({
    defaultMessage: 'Terms and conditions',
    description: 'Page title for the terms and conditions page',
  }),
  privacy: defineMessage({
    defaultMessage: 'Privacy Policy',
    description: 'Page title for the privacy policy page',
  }),
  cancellation: defineMessage({
    defaultMessage: 'Cancellation Policy',
    description: 'Page title for the cancellation policy page',
  }),
};

export function meta({ loaderData, matches }: Route.MetaArgs) {
  const root = matches.find((m) => m?.id === 'root')?.loaderData as
    | RootLoaderData
    | undefined;
  if (!root) return [];

  // For the general terms the type is undefined, so the path resolves to
  // `/terms`; privacy/cancellation resolve to `/terms/privacy` etc.
  const type = loaderData?.type === 'general' ? undefined : loaderData?.type;

  return buildPageSeo({
    storefront: {
      fqdn: root.fqdn,
      languages: root.languages,
      defaultLanguage: root.defaultLanguage,
      resolvedLocale: root.locale,
      companyName: root.companyName,
      preview: root.preview,
      seoEnabled: root.preferences.seo,
    },
    buildPath: (locale) =>
      localeHref(locale, '/:locale?/terms/:type?', { type }),
    pageTitle: loaderData?.title,
  });
}

export default function Terms({ loaderData }: Route.ComponentProps) {
  const { content, title, type } = loaderData;
  const { preview } = useRootData();

  return (
    <PageCard>
      <Stack gap="medium">
        <Text style="headline" as="h1">
          {title}
        </Text>
        <Markdown>{content}</Markdown>
        {type === 'privacy' && (
          <CookieDeclaration enabled={isCookieConsentEnabled({ preview })} />
        )}
      </Stack>
    </PageCard>
  );
}
